import { AuthProvider, useAuth, useAuthUpdate } from "context/AuthContext";
import { jwtDecode } from "jwt-decode";
import AppealDetail from "pages/Appeal/AppealDetail";
import DefendantAppealList from "pages/Appeal/DefendantApealList";
import DefendantAppealDetail from "pages/Appeal/DefendantAppealDetail";
import HigherAdminList from "pages/Appeal/HigherAdminList";
import HigherAdminDetail from "pages/Appeal/HiigherAdminDetail";
import ProsecutorAppealList from "pages/Appeal/ProsecutorApealList";
import BailRightDetail from "pages/BailRight/BailRightDetail";
import BailRightList from "pages/BailRight/BailRightList";
import BailRightRequest from "pages/BailRight/BailRightRequest";
import CompliantList from "pages/Compliant/CompliantList";
import OpeningCaseFilesDetail from "pages/Court/OpeningCaseFilesDetail";
import OpeningCaseFileList from "pages/Court/OpeningCaseFilesList";
import ExternalPoliceList from "pages/ExternalPolice/ExternalPoliceList";
import ClerkIncident from "pages/Incident/ClerkIncident/ClerkIncident";
import AddCourtDetail from "pages/InstituteOfCharge/AddCourtDetail";
import RequestInstituteOfCharge from "pages/InstituteOfCharge/RequestInstituteOfCharge";
import InvestigationFileDetail from "pages/InvestigationFileDetail/InvestigationFileDetail";
import InvestigationFileList from "pages/InvestigationFileList/InvestigationFileList";
import InvestigationList from "pages/InvestigationList/InvestigationList";
import ScheduleList from "pages/Schedule/ScheduleList";
import SuspectToWitnessRequest from "pages/SuspectToWitness/SuspectToWitnessRequest";
import WitnessProtectionDetail from "pages/witnessProtection/WitnessProtectionDetail";
import WitnessProtectionList from "pages/witnessProtection/WitnessProtectionList";
import WitnessProtectionRequest from "pages/witnessProtection/WitnessProtectionRequest";
import { useEffect, useState } from "react";
import useLanguageListener from "./hooks/useLanguageListener";

import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { fetchStructure } from "services/authService";
import { LoginAccount, Roles } from "types";
import { hasRole } from "utils/helper";
// import Header from "./components/Header";
import Header from "components/Header";
import { NotificationProvider } from "context/NotificationContext";
import { TooltipProvider } from "context/TooltipProvider";
import AssetAppealContainer from "pages/AssetAppeal/AssetAppealContainer";
import AssetAppealList from "pages/AssetAppeal/ProsecutorAppeal/AssetAppealList";
import RespondentAssetAppealContainer from "pages/AssetAppeal/RespondentAppeal/RespondentAppealContainer";
import RespondentAppealList from "pages/AssetAppeal/RespondentAppeal/RespondentAppealList";
import AssetRecoveryRegistration from "pages/AssetRecovery/AssetRecoveryRegistration/AssetRecoveryRegistration";
import AssetReferal from "pages/AssetRecovery/AssetReferralList/AssetReferralList";
import DashboardPage from "pages/Dashboard/dashboardPage";
import FurtherInvestigationDetailForPolice from "pages/FurtherInvestigationList/FurtherInvestigationDetailForPolice";
import FurtherInvestigationListForPolice from "pages/FurtherInvestigationList/FurtherInvestigationListForPolice";
import IncidentByMobileDetail from "pages/IncidentByMobile/IncidentByMobileDetail";
import IncidentByMobileList from "pages/IncidentByMobile/IncidentByMobileList";
import UpdateCourtDetail from "pages/InstituteOfCharge/UpdateCourtDetail";
import Sidebar from "./components/Sidebar";
import IncidentDetail from "./pages/Incident/IncidentDetail/IncidentDetail";
import IncidentList from "./pages/Incident/IncidentList/IncidentList";
import IncidentRegister from "./pages/Incident/IncidentRegister/IncidentRegister";
import InstituteOfChargeListForSidebar from "pages/InstituteOfCharge/InstituteOfChargeListForsidebar";
import UpcomingHearingsList from "pages/UpcomingHearings/UpcomingHearingsList";
import InstituteOfChargeDetailFromSidebar from "pages/InstituteOfCharge/InstituteOfChargeDetailFromSidebar";
import CassationAppealList from "pages/Appeal/CassationApealList";
import CrimeSearch from "pages/CrimeSearch/CrimeSearch";
import SessionTimeout from "./components/SessionTimeout";

export interface JwtPayload {
  exp?: number; // Expiration time in seconds
  iat?: number; // Issued at
  sub?: string; // Subject (user id, etc.)
  [key: string]: any; // Any additional claims
}

export function isValidJwtFormat(token: string): boolean {
  if (!token || typeof token !== "string") return false;

  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Check if all parts are non-empty
  return parts.every((part) => part.trim().length > 0);
}

export function getTokenPayloadSafely(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token || !isValidJwtFormat(token)) {
    // console.warn("Invalid or missing token format");
    return null;
  }

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    // console.error("Error decoding token:", error);
    return null;
  }
}

export function decodeAccessToken(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token) return null;

  try {
    return getTokenPayloadSafely(token);
  } catch (err) {
    // console.error("Failed to decode access token:", err);

    // Fallback to manual decoding
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) return null;

      const base64Url = tokenParts[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
          .join(""),
      );

      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (fallbackErr) {
      // console.error(
      //   "Failed to decode access token with fallback method:",
      //   fallbackErr
      // );
      return null;
    }
  }
}

function AppContent() {
  useLanguageListener();

  const { setLoginData } = useAuth();
  const navigate = useNavigate();
  const contextData = useAuth();
  const updateAuthContext = useAuthUpdate();
  // const { user } = useAuth();

  const [isMember, setIsMember] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  const fetchStructureData = async (admin_unit: string, org_unit: string) => {
    try {
      const data = await fetchStructure(admin_unit + "", org_unit + "");
      return data;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    // console.log("handle message effect");
    async function handleMessage(event: MessageEvent) {
      // console.log(
      //   "token:",
      //   event.data?.token || event?.data?.data?.access_token
      // );
      // console.log("data", event.data);
      // console.log("event", event);
      const token = event?.data?.data?.access_token || event.data?.token;
      if (
        event.data?.type === "SET_SESSION_DATA_FOR_PROSECUTION" ||
        event.data?.type === "SET_TOKEN"
      ) {
        // console.log("token", token);

        if (token && isValidJwtFormat(token)) {
          // console.log("Token received and stored");
          const payload = getTokenPayloadSafely(token);
          // console.log("payload", payload);

          const structure = await fetchStructureData(
            (
              payload?.admin_unit || payload?.organization?.admin_unit
            ).toString(),
            payload?.org_unit ? payload?.org_unit.toString() : +"",
          );

          const isPoliceAllowed =
            payload?.is_police === true &&
            (payload?.role === "Member" || payload?.role === "Admin");

          const isInternalUser = payload?.user_type === "internal";

          if (isPoliceAllowed || isInternalUser) {
            // ✅ allowed to proceed
          } else {
            setServiceUnavailable(true);
            return;
          }

          // sessionStorage.setItem(
          //   "authToken",
          //   JSON.stringify({
          //     ...payload,
          //     access: payload?.access || payload?.access_token,
          //     refresh: payload?.refresh || payload?.refresh_token,
          //     type: payload?.access ? "Main" : "Police",
          //     structure: structure,
          //   })
          // ); // Store the token in sessionStorage

          setLoginData({
            ...payload,
            access: payload?.access || payload?.access_token || token,
            refresh: payload?.refresh || payload?.refresh_token || token,
            type:
              payload?.role === "Member" || payload?.is_police
                ? "Police"
                : "Main",
            structure,
          } as any);

          setIsMember(!payload?.access);

          sessionStorage.setItem(
            "authToken",
            event.data?.token || event?.data?.data?.access_token,
          );

          setIsMember(
            payload?.role === "Member" || payload?.is_police ? true : false,
          );

          navigate(
            payload?.role === "Member" || payload?.is_police
              ? "/investigation-files"
              : "/",
            {
              replace: true,
            },
          );

          // console.log("Token payload:", payload);
          // if (payload?.user_type === "internal") {
          //   setInternalUserType(true);
          // }
          // setTokenLoaded(true);
        } else {
          // console.error("Invalid token received in SET_TOKEN message");
          setTokenLoaded(true);
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Check for existing token on initial load
  useEffect(() => {
    const hasToken =
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("access_token");

    if (hasToken) {
      const payload = getTokenPayloadSafely(hasToken);

      setIsAuthenticated(true);
      updateAuthContext({
        ...contextData,
        token: hasToken,
        loginData: payload as LoginAccount,
      });
    }
  }, []);

  // const handleLoginSuccess = () => {
  //   setIsAuthenticated(true);
  //   navigate("/conduct-legal-research"); // Redirect to main page after login
  // };

  // const handleLogout = () => {
  //   sessionStorage.removeItem("authToken");
  //   setIsAuthenticated(false);
  //   updateAuthContext({
  //     ...contextData,
  //     token: "",
  //     loginData: {} as LoginAccount,
  //   });
  // };

  // const { isAuthenticated, setLoginData, logout } = useAuth();
  // const navigate = useNavigate();

  // const [loading, setLoading] = useState<boolean>(false);

  // const fetchUserStructure = (admin_unit: string, org_unit: string) => {
  //   fetchStructure(admin_unit + "", org_unit + "")
  //     .then((data) => {
  //       return data;
  //     })
  //     .catch(() => {
  //       return null;
  //     });
  // };

  // const handleLoginSuccess = async (loginData: LoginAccount) => {
  //   if (!loginData) return;
  //   const structure = await fetchStructure(
  //     loginData?.admin_unit + "",
  //     loginData?.org_unit + ""
  //   );

  //   sessionStorage.setItem(
  //     "authToken",
  //     JSON.stringify({
  //       ...loginData,
  //       access: loginData?.access || loginData?.access_token,
  //       refresh: loginData?.refresh || loginData?.refresh_token,
  //       type: loginData?.access ? "Main" : "Police",
  //       structure: structure,
  //     })
  //   ); // Store the token in sessionStorage

  //   setLoginData({
  //     ...loginData,
  //     access: loginData?.access || loginData?.access_token,
  //     refresh: loginData?.refresh || loginData?.refresh_token,
  //     type: loginData?.access ? "Main" : "Police",
  //     structure,
  //   });
  //   const isClerk = hasRole(loginData?.roles, [
  //     Roles.variousClerk,
  //     Roles.economicClerk,
  //     Roles.corruptionClerk,
  //   ]);
  //   navigate(isClerk ? "/investigation-files" : "/investigation-files", {
  //     replace: true,
  //   }); // Redirect to the main page after login
  // };

  // if (!isAuthenticated) {
  //   return (
  //     <LoginPage
  //       onLoginSuccess={handleLoginSuccess}
  //       loading={loading}
  //       setLoading={setLoading}
  //     />
  //   );
  // }

  const appRoutes = (
    <>
      <SessionTimeout app="prosecution" />
    <Routes>
      <Route path="/" element={<DashboardPage />} />
      <Route path="/incident-list" element={<IncidentList />} />
      {/* <Route path="/incident-request" element={<IncidentList />} /> */}
      <Route path="/incident-register" element={<IncidentRegister />} />
      <Route path="/incident/:incidentId" element={<IncidentDetail />} />

      <Route path="/incident-list" element={<IncidentList />} />
      <Route path="/investigation-list" element={<InvestigationList />} />

      <Route path="/charges" element={<InstituteOfChargeListForSidebar />} />
      <Route
        path="/upcoming-hearings"
        element={<UpcomingHearingsList />}
      />
      <Route
        path="/charges/:caseId"
        element={<InstituteOfChargeDetailFromSidebar />}
      />

      <Route
        path="/economic-coruption-investigation"
        element={<InvestigationList />}
      />
      <Route path="/incident-register" element={<IncidentRegister />} />
      <Route path="/incident/:incidentId" element={<IncidentDetail />} />
      <Route path="/investigation-files" element={<InvestigationFileList />} />
      <Route path="/record-incident/:incidentId" element={<ClerkIncident />} />
      <Route
        path="/investigation-files/:id"
        element={<InvestigationFileDetail />}
      />
      <Route path="/schedules" element={<ScheduleList />} />
      <Route
        path="/request-institute-of-charge"
        element={<RequestInstituteOfCharge />}
      />
      {/* <Route
                path="/request-institute-of-charge/:id"
                element={<InstituteOfChargeDetail />}
              /> */}
      <Route
        path="/further-investigation"
        element={<FurtherInvestigationListForPolice />}
      />
      <Route
        path="/further-investigation/:furtherInvestigationId"
        element={<FurtherInvestigationDetailForPolice />}
      />

      {/* <Route path="/cases" element={<InstituteOfChargeList />} /> */}
      {/* <Route
                path="/cases/:caseId"
                element={<InstituteOfChargeDetail />}
              /> */}
      <Route path="/court-detail" element={<AddCourtDetail />} />
      <Route path="/update-court-detail" element={<UpdateCourtDetail />} />
      <Route path="/courts" element={<OpeningCaseFileList />} />
      <Route path="/courts/:courtId" element={<OpeningCaseFilesDetail />} />
      <Route path="/external-police" element={<ExternalPoliceList />} />
      <Route path="/bail-right" element={<BailRightList />} />
      <Route path="/bail-right/:id" element={<BailRightDetail />} />
      <Route path="/bail-right-request" element={<BailRightRequest />} />
      <Route path="/compliant" element={<CompliantList />} />
      {/* <Route
                path="/suspect-to-witness"
                element={<SuspectToWitnessList />}
              />
              <Route
                path="/suspect-to-witness/:id"
                element={<SuspectToWitnessDetail />}
              /> */}
      <Route
        path="/suspect-to-witness-request"
        element={<SuspectToWitnessRequest />}
      />
      {/* <Route path="/doubtful" element={<DoubtfulList />} /> */}
      {/* <Route path="/doubtful-request" element={<DoubtfulRequest />} /> */}
      {/* <Route path="/doubtful/:id" element={<DoubtfulDetail />} /> */}

      <Route path="/prosecutor-appeal" element={<ProsecutorAppealList />} />
      <Route path="/prosecutor-appeal/:id" element={<AppealDetail />} />
      <Route path="/defendant-appeal" element={<DefendantAppealList />} />
      <Route path="/defendant-appeal/:id" element={<DefendantAppealDetail />} />
      <Route path="/higher-admin" element={<HigherAdminList />} />
      <Route path="/higher-admin/:id" element={<HigherAdminDetail />} />

      <Route path="/cassation-appeal" element={<CassationAppealList />} />
      <Route path="/cassation-appeal/:id" element={<AppealDetail />} />
      <Route
        path="/defendant-cassation"
        element={<DefendantAppealList isCassation />}
      />
      <Route
        path="/defendant-cassation/:id"
        element={<DefendantAppealDetail />}
      />

      <Route path="/witness-protection" element={<WitnessProtectionList />} />
      <Route
        path="/witness-protection/:id"
        element={<WitnessProtectionDetail />}
      />
      <Route
        path="/witness-protection/request"
        element={<WitnessProtectionRequest />}
      />

      <Route path="/asset-recovery" element={<AssetReferal />} />
      <Route path="/asset-register" element={<AssetRecoveryRegistration />} />
      {/* <Route
                path="/asset-register/:id"
                element={<AssetRecoveryRegistration />}
              /> */}
      <Route
        path="/asset-recovery/:id?"
        element={<AssetRecoveryRegistrationWrapper />}
      />

      {/* Asset Appeal Routes */}
      <Route path="/asset-appeal" element={<AssetAppealList />} />
      <Route path="/asset-appeal/:id" element={<AssetAppealContainer />} />
      <Route
        path="/asset-appeal-respondent"
        element={<RespondentAppealList />}
      />
      <Route
        path="/asset-appeal-respondent/:id"
        element={<RespondentAssetAppealContainer />}
      />

      <Route path="/crime-search" element={<CrimeSearch />} />

      <Route path="/incident-by-mobile" element={<IncidentByMobileList />} />
      <Route
        path="/incident-by-mobile/:id"
        element={<IncidentByMobileDetail />}
      />

      <Route
        path="/login"
        element={<Navigate to="/investigation-files" replace />}
      />
    </Routes>
    </>
  );

  if (serviceUnavailable) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">
            Service Unavailable
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-6">
            You do not have permission to access this service or it is currently
            unavailable for your account.
          </p>

          {/* Optional action */}
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-700 transition"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-screen bg-gray-100">
        <div className="h-screen flex bg-gray-100">
          {/* Fixed Sidebar */}
          <div className="hidden md:block">
            <Sidebar />
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="fixed inset-0 z-40 md:hidden">
              <div
                className="fixed inset-0 bg-gray-600 bg-opacity-75"
                onClick={() => setSidebarOpen(false)}
              ></div>
              <div className="fixed inset-y-0 left-0 z-50 w-64">
                <Sidebar />
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Fixed Header */}
            {!isMember && <Header isInternalUser={true} />}

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Mobile Menu Button */}
              <div className="md:hidden mb-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="mobile-menu-btn"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
              {appRoutes}
            </main>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}

const AssetRecoveryRegistrationWrapper = () => {
  const { id } = useParams();
  return <AssetRecoveryRegistration key={id} />;
};
